import React, { useState, useEffect } from "react";
import {
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  CircularProgress,
  makeStyles
} from "@material-ui/core";
import { toast } from "react-toastify";
import { i18n } from "../../translate/i18n";
import {
  updateInactivityTimeout,
  getInactivityTimeout
} from "../../services/inactivityService";

const useStyles = makeStyles((theme) => ({
  paper: {
    padding: theme.spacing(3),
    marginBottom: theme.spacing(2)
  },
  title: {
    marginBottom: theme.spacing(2),
    fontWeight: 500
  },
  description: {
    marginBottom: theme.spacing(3),
    color: theme.palette.text.secondary
  },
  formContainer: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2)
  },
  textField: {
    maxWidth: 300
  },
  buttonContainer: {
    display: "flex",
    gap: theme.spacing(2),
    marginTop: theme.spacing(2)
  },
  helperText: {
    marginTop: theme.spacing(1),
    color: theme.palette.text.secondary,
    fontSize: "0.875rem"
  }
}));

const InactivityTimeoutPanel = () => {
  const classes = useStyles();
  const [minutes, setMinutes] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    loadInactivityTimeout();
  }, []);

  const loadInactivityTimeout = async () => {
    try {
      setLoadingData(true);
      const data = await getInactivityTimeout();
      if (data.inactivityTimeoutMinutes !== null) {
        setMinutes(String(data.inactivityTimeoutMinutes));
      }
    } catch (err) {
      console.error("Error loading inactivity timeout:", err);
      toast.error(i18n.t("inactivityTimeout.errors.loadFailed"));
    } finally {
      setLoadingData(false);
    }
  };

  const validateInput = (value) => {
    if (value === "") {
      setError("");
      return true;
    }

    const num = Number(value);
    
    if (isNaN(num)) {
      setError(i18n.t("inactivityTimeout.errors.mustBeNumber"));
      return false;
    }

    if (!Number.isInteger(num)) {
      setError(i18n.t("inactivityTimeout.errors.mustBeInteger"));
      return false;
    }

    if (num <= 0) {
      setError(i18n.t("inactivityTimeout.errors.mustBePositive"));
      return false;
    }

    if (num > 10000) {
      setError(i18n.t("inactivityTimeout.errors.tooLarge"));
      return false;
    }

    setError("");
    return true;
  };

  const handleChange = (e) => {
    const value = e.target.value;
    setMinutes(value);
    validateInput(value);
  };

  const handleSave = async () => {
    if (!validateInput(minutes)) {
      return;
    }

    try {
      setLoading(true);
      const timeoutValue = minutes === "" ? null : Number(minutes);
      await updateInactivityTimeout(timeoutValue);
      toast.success(i18n.t("inactivityTimeout.success.saved"));
    } catch (err) {
      console.error("Error saving inactivity timeout:", err);
      toast.error(i18n.t("inactivityTimeout.errors.saveFailed"));
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    setMinutes("");
    setError("");
  };

  if (loadingData) {
    return (
      <Paper className={classes.paper}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
          <CircularProgress />
        </Box>
      </Paper>
    );
  }

  return (
    <Paper className={classes.paper}>
      <Typography variant="h6" className={classes.title}>
        {i18n.t("inactivityTimeout.title")}
      </Typography>
      
      <Typography variant="body2" className={classes.description}>
        {i18n.t("inactivityTimeout.description")}
      </Typography>

      <Box className={classes.formContainer}>
        <TextField
          label={i18n.t("inactivityTimeout.label")}
          type="number"
          value={minutes}
          onChange={handleChange}
          error={!!error}
          helperText={error || i18n.t("inactivityTimeout.helperText")}
          className={classes.textField}
          variant="outlined"
          disabled={loading}
          InputProps={{
            inputProps: { 
              min: 1,
              max: 10000,
              step: 1
            }
          }}
        />

        <Box className={classes.buttonContainer}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSave}
            disabled={loading || !!error}
          >
            {loading ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              i18n.t("inactivityTimeout.buttons.save")
            )}
          </Button>
          
          <Button
            variant="outlined"
            onClick={handleClear}
            disabled={loading}
          >
            {i18n.t("inactivityTimeout.buttons.clear")}
          </Button>
        </Box>

        <Typography variant="caption" className={classes.helperText}>
          {i18n.t("inactivityTimeout.note")}
        </Typography>
      </Box>
    </Paper>
  );
};

export default InactivityTimeoutPanel;
